import { ListModel } from "shared/interfaces/core-interfaces";
import yup from 'shared/validation/yupExtensions';

// Enums matching backend
export enum Process {
    Processing = 1,
    Release = 2,
    Control = 3,
    Information = 4
}

export enum FileUploadMode {
    SimpleUpload = 1,
    UploadAndShare = 2
}

export enum FileStatus {
    Uploading = 1,
    Completed = 2,
    Expired = 3,
    Aborted = 4
}

// Request DTOs
export interface InitializeUploadRequest {
    projectId: string;
    process: Process;
    mode: FileUploadMode;
    fileName: string;
    fileSize: number;
    subject?: string | null;
    body?: string | null;
    recipientIds?: string[] | null;
    additionalRecipients?: string[] | null;
}

export interface CompleteUploadRequest {
    fileUploadId: string;
    projectId: string;
    uploadId: string;
    partETags: PartETagDto[];
}

export interface PartETagDto {
    partNumber: number;
    eTag: string;
}

export interface AbortUploadRequest {
    fileUploadId: string;
    projectId: string;
    uploadId: string;
}

// Response DTOs
export interface InitializeFileUploadResponse {
    fileId: string;
    uploadId: string;
}

export interface AppendChunkResponse {
    partNumber: number;
    eTag: string;
}

export interface CompleteUploadResponse {
    fileUploadId: string;
}

export interface ProjectFileUploadResponse {
    id: string;
    projectId: string;
    project: string;
    uploaderId: string;
    uploader: string;
    fileName: string;
    process: string;
    status: string;
    uploadedAt: Date;
    expiresAt: Date;
}

export interface ProjectFileUploadDetailedResponse extends ProjectFileUploadResponse {
    subject?: string | null;
    body?: string | null;
    additionalRecipients: string[];
    recipients: ListModel<string>[];
}

// Create data response (dropdown options)
export interface ProjectFilesCreateDataResponse {
    projects: ListModel<string>[];
    employees: ListModel<string>[];
    processes: ListModel<number>[];
}

// Slice state
export interface ProjectFilesState {
    fileUploads: ProjectFileUploadResponse[];
    fileUpload: ProjectFileUploadDetailedResponse | null;
    createData: ProjectFilesCreateDataResponse | null;
}

export const initialProjectFilesState: ProjectFilesState = {
    fileUploads: [],
    fileUpload: null,
    createData: null,
};

// Form request model
export interface ProjectFileUploadFormRequest {
    uploaderId: string;
    projectId: string;
    process: number;
    file: File | null;
    mode: FileUploadMode;
    recipientIds: string[];
    additionalRecipients: string[];
    subject: string;
    body: string;
}

export const initProjectFileUploadFormRequest: ProjectFileUploadFormRequest = {
    uploaderId: '',
    projectId: '',
    process: 0,
    file: null,
    mode: FileUploadMode.SimpleUpload,
    recipientIds: [],
    additionalRecipients: [],
    subject: '',
    body: '',
};

export const ProjectFileUploadModelScheme = yup.object().shape({
    uploaderId: yup.string().required("Uploader is required."),
    projectId: yup.string().required("Profit/Cost center is required."),
    process: yup.number().required("Process is required.").min(1, "Process is required."),
    file: yup.mixed<File>().required("Attachment is required.").nullable()
        .test('fileRequired', 'Attachment is required.', (value) => value !== null && value !== undefined)
        .test('fileSize', 'File size must be greater than 0.', (value) => !value || (value as File).size > 0),
    mode: yup.number().required(),
    recipientIds: yup.array().of(yup.string().required()).when('mode', {
        is: FileUploadMode.UploadAndShare,
        then: (schema) => schema.test(
            'recipientIds-or-additional',
            // 'At least one recipient or additional recipient is required.',
            '',
            function (value) {
                const additional = this.parent.additionalRecipients as string[] | undefined;
                return (value && value.length > 0) || (additional && additional.length > 0);
            }
        ),
        otherwise: (schema) => schema.optional(),
    }),
    additionalRecipients: yup.array().of(yup.string().email("Invalid email address.").required()).when('mode', {
        is: FileUploadMode.UploadAndShare,
        then: (schema) => schema.test(
            'additional-or-recipientIds',
            // 'At least one recipient or additional recipient is required.',
            '',
            function (value) {
                const recipients = this.parent.recipientIds as string[] | undefined;
                return (value && value.length > 0) || (recipients && recipients.length > 0);
            }
        ),
        otherwise: (schema) => schema.optional(),
    }),
    subject: yup.string().when('mode', {
        is: FileUploadMode.UploadAndShare,
        then: (schema) => schema.required("Email subject is required."),
        otherwise: (schema) => schema.optional(),
    }),
    body: yup.string().when('mode', {
        is: FileUploadMode.UploadAndShare,
        then: (schema) => schema.required("Email body is required."),
        otherwise: (schema) => schema.optional(),
    }),
});
